import { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import i18n from "../../i18n-config";
import {
  CustomButton,
  CustomDateField,
  CustomDropdown,
  CustomDropzone,
  CustomLoader,
  CustomPopper,
  CustomRadio,
  CustomSearchBar,
  SvgContainer,
} from "../../components";
import theme from "../../theme/theme";
import { LOCATION } from "../../assets/icons";
import SigninTextInput from "../../components/SigninTextInput";
import { capitalizeFirstLetter } from "../../utils/strings";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/state";
import { createAsyncOffre, Offre } from "../../store/features/offreSlice";
import notifyMessage from "../../utils/notify";
import { ArrowDropDown } from "@mui/icons-material";
import BaraBossCard from "./component/BaraBossCard";
import { useNavigate } from "react-router-dom";

function BaraForm() {
  const super_categories = useSelector((state: RootState) =>
    Object.values(state.super_categorie)
  );
  const boss = useSelector((state: RootState) => Object.values(state.boss));
  const categorie = useSelector((state: RootState) =>
    Object.values(state.categorie)
  );
  const grouilleurs = useSelector((state: RootState) =>
    Object.values(state.grouilleur)
  );
  const user = useSelector((state: RootState) => state.auth.user);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [filterBoss, setFilterBoss] = useState("");
  const [filterGrouilleur, setFilterGrouilleur] = useState("");
  const [filterGrouilleurBara, setFilterGrouilleurBara] = useState("");
  const [selectedBoss, setSelectedBoss] = useState(null) as any;
  const [selectedGrouilleur, setSelectedGrouilleur] = useState(null) as any;
  const [selectedGrouilleurBara, setSelectedGrouilleurBara] = useState(null) as any;
  const [formData, setFormData] = useState<Offre>({
    titre: "",
    description: "",
    adresse: "",
    localisation: "",
    dateDemarrageSouhaiter: "",
    paiementGrouilleur: "JOURNEE",
    avecMateriel: false,
    devis: false,
    previsite: false,
    idSousCategorie: undefined,
    idCategorie: undefined,
  });
  const [show, setShow] = useState(false);
  const handleShowOpen = () => setShow(!show);
  const [showGrouilleur, setShowGrouilleur] = useState(false);
  const [showGrouilleurBara, setShowGrouilleurBara] = useState(false);
  const handleShowGrouilleur = () => setShowGrouilleur(!showGrouilleur);
  const handleShowGrouilleurBara = () => setShowGrouilleurBara(!showGrouilleurBara);

  const bossSearch = useMemo(
    () =>
      boss.filter(
        (val: any) =>
          val.nom?.toLowerCase()?.includes(filterBoss.toLowerCase()) ||
          val.prenom?.toLowerCase()?.includes(filterBoss.toLowerCase())
      ),
    [filterBoss, boss]
  );

  const grouilleurSearch = useMemo(
    () =>
      grouilleurs.filter(
        (val: any) =>
          val.nom?.toLowerCase()?.includes(filterGrouilleur.toLowerCase()) ||
          val.prenom?.toLowerCase()?.includes(filterGrouilleur.toLowerCase())
      ),
    [filterGrouilleur, grouilleurs]
  );

  const grouilleurBaraSearch = useMemo(
    () =>
      grouilleurs.filter(
        (val: any) =>
          val.nom?.toLowerCase()?.includes(filterGrouilleurBara.toLowerCase()) ||
          val.prenom?.toLowerCase()?.includes(filterGrouilleurBara.toLowerCase())
      ),
    [filterGrouilleurBara, grouilleurs]
  );

  const toggleCheckbox = (keyval: string, value: boolean) => {
    setFormData({ ...formData, [keyval]: value });
  };

  const toggleLoader = () => {
    setLoading(!loading);
  };

  const handleClose = () => {
    navigate("/bara")
  };

  const onSelectBoss = (item: any) => {
    handleShowOpen();
    setSelectedBoss(item);
  };

  const onSelectGrouilleur = (item: any) => {
    handleShowGrouilleur();
    setSelectedGrouilleur(item);
  };

  const onSelectGrouilleurBara = (item: any) => {
    handleShowGrouilleurBara();
    setSelectedGrouilleurBara(item);
  };

  const onSearchBoss = (value: string) => {
    setFilterBoss(value);
  };

  const onSearchGrouilleur = (value: string) => {
    setFilterGrouilleur(value);
  };

  const onSearchGrouilleurBara = (value: string) => {
    setFilterGrouilleurBara(value);
  };

  const callback = () => {
    setLoading(false);
    handleClose();
    notifyMessage({
      message: capitalizeFirstLetter(i18n.t("create_success")),
      severity: "success",
    });
  };

  const onError = () => {
    setLoading(false);
    notifyMessage({
      message: capitalizeFirstLetter(i18n.t("error_occured")),
      severity: "error",
    });
  };

  const onChangeField = (key: string, value: string | number) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const onSubmit = () => {
    const toSave = {
      ...formData,
      idGrouilleurDePreference: selectedGrouilleur?.id,
      idBoss: selectedBoss?.id,
      idGrouilleur: selectedGrouilleurBara?.id
    };

    toggleLoader();
    dispatch(createAsyncOffre(toSave, callback, onError) as any);
  };

  const disabled =
    !formData.titre.trim() ||
    !formData.description.trim() ||
    !formData.adresse.trim() || !selectedBoss?.id;

  return (
    <Box sx={{ backgroundColor: "#F9F9F9" }} p={3} pt={6}>
      <Typography fontSize={22} fontWeight="600" mb={2}>
        {i18n.t("ajouter_bara")}
      </Typography>
      <Box
        sx={{
          p: 5,
          backgroundColor: "white",
          boxShadow: "0px 4px 4px rgba(139, 139, 139, 0.05)",
          borderRadius: "5px",
        }}
      >
        <Grid container spacing={5}>
          <Grid xs={6} item>
            <CustomDropdown
              title={capitalizeFirstLetter(i18n.t("categorie"))}
              value={formData.idCategorie}
              items={super_categories}
              isDynamic
              containerStyle={{ marginTop: 0 }}
              handleChange={(value: string) =>
                onChangeField("idCategorie", Number(value))
              }
            />
            <SigninTextInput
              title={capitalizeFirstLetter(i18n.t("nom_du_bara"))}
              value={formData.titre}
              onChange={(value: string) => onChangeField("titre", value)}
            />
            <SigninTextInput
              title={"Description"}
              value={formData.description}
              onChange={(value: string) => onChangeField("description", value)}
              rootStyle={{ height: "auto" }}
              inputStyle={{ height: 150, marginBottom: 5 }}
              multiline
              rows={6}
            />
            <CustomDateField
              title={capitalizeFirstLetter(i18n.t("date_demarrage_souhaite"))}
              value={formData.dateDemarrageSouhaiter}
              onChange={(value: string) =>
                onChangeField("dateDemarrageSouhaiter", value)
              }
            />
            <Typography
              color="black"
              sx={{
                fontSize: {
                  xs: 12,
                  md: 14,
                },
                mb: 1,
                mt: 3,
              }}
            >
              {capitalizeFirstLetter(i18n.t("boss"))}
            </Typography>
            <CustomPopper
              icon={
                <OutlinedInput
                  disabled
                  onClick={handleShowOpen}
                  endAdornment={<ArrowDropDown />}
                  fullWidth
                  value={`${selectedBoss?.nom ?? ""} ${
                    selectedBoss?.prenom ?? ""
                  }`}
                />
              }
              onClose={handleShowOpen}
              containerStyle={{ padding: 0, borderRadius: "5px" }}
              buttonStyle={{
                backgroundColor: "transparent",
                width: "100%",
                padding: 0,
              }}
            >
              <Box
                p={2}
                sx={{
                  height: "400px",
                  width: "400px",
                  position: "relative",
                  overflow: "auto",
                }}
              >
                <CustomSearchBar
                  placeholder={capitalizeFirstLetter(i18n.t("rechercher"))}
                  ctnStyle={{ width: "100%", marginBottom: 15 }}
                  onChange={(event) => onSearchBoss(event.target.value)}
                />
                {bossSearch.map((item: any) => (
                  <BaraBossCard
                    key={item.id}
                    item={item}
                    onClick={() => onSelectBoss(item)}
                  />
                ))}
              </Box>
            </CustomPopper>
            <Typography
              color="black"
              sx={{
                fontSize: {
                  xs: 12,
                  md: 14,
                },
                mb: 1,
                mt: 3,
              }}
            >
              {capitalizeFirstLetter(i18n.t("select_grouilleur_pref"))}
            </Typography>
            <CustomPopper
              icon={
                <OutlinedInput
                  disabled
                  onClick={handleShowGrouilleur}
                  endAdornment={<ArrowDropDown />}
                  fullWidth
                  value={`${selectedGrouilleur?.nom ?? ""} ${
                    selectedGrouilleur?.prenom ?? ""
                  }`}
                />
              }
              onClose={handleShowGrouilleur}
              containerStyle={{ padding: 0, borderRadius: "5px" }}
              buttonStyle={{
                backgroundColor: "transparent",
                width: "100%",
                padding: 0,
              }}
            >
              <Box
                p={2}
                sx={{
                  height: "400px",
                  width: "400px",
                  position: "relative",
                  overflow: "auto",
                }}
              >
                <CustomSearchBar
                  placeholder={capitalizeFirstLetter(i18n.t("rechercher"))}
                  ctnStyle={{ width: "100%", marginBottom: 15 }}
                  onChange={(event) => onSearchGrouilleur(event.target.value)}
                />
                {grouilleurSearch.map((item: any) => (
                  <BaraBossCard
                    key={item.id}
                    item={item}
                    onClick={() => onSelectGrouilleur(item)}
                  />
                ))}
              </Box>
            </CustomPopper>
          </Grid>
          <Grid xs={6} item>
            <CustomDropdown
              title={capitalizeFirstLetter(i18n.t("categorie_sous"))}
              value={formData.idSousCategorie}
              items={categorie}
              isDynamic
              containerStyle={{ marginTop: 0 }}
              handleChange={(value: string) =>
                onChangeField("idSousCategorie", Number(value))
              }
            />
            <SigninTextInput
              title={capitalizeFirstLetter(i18n.t("adress_bara"))}
              value={formData.adresse}
              onChange={(value: string) => onChangeField("adresse", value)}
              icon={
                <SvgContainer
                  svg={LOCATION}
                  width={15}
                  color={theme.colors.dimgray + "80"}
                />
              }
            />
            <SigninTextInput
              title={capitalizeFirstLetter(i18n.t("localisation"))}
              value={formData.localisation}
              onChange={(value: string) => onChangeField("localisation", value)}
              icon={
                <SvgContainer
                  svg={LOCATION}
                  width={15}
                  color={theme.colors.dimgray + "80"}
                />
              }
            />
            <Box
              mt={3}
              sx={{
                backgroundColor: theme.colors.silver + "30",
                px: 2,
                py: 1,
                borderRadius: 2,
              }}
            >
              <Typography fontSize={13}>{i18n.t("grouilleur_doit")}</Typography>
              <Stack direction={"row"} mt={2}>
                <CustomRadio
                  label={i18n.t("avec_materiel")}
                  containerStyle={{ marginRight: 8 }}
                  checked={formData.avecMateriel}
                  onClick={() => toggleCheckbox("avecMateriel", true)}
                />
                <CustomRadio
                  label={i18n.t("sans_materiel")}
                  checked={!formData.avecMateriel}
                  onClick={() => toggleCheckbox("avecMateriel", false)}
                />
              </Stack>
            </Box>

            <Stack direction={"row"} mt={3} justifyContent="space-between">
              <Box>
                <Typography fontSize={13}>
                  {i18n.t("previsite_lieu")}
                </Typography>
                <Stack direction={"row"} mt={2}>
                  <CustomRadio
                    label={i18n.t("oui")}
                    containerStyle={{ marginRight: 5 }}
                    checked={formData.previsite}
                    onClick={() => toggleCheckbox("previsite", true)}
                  />
                  <CustomRadio
                    label={i18n.t("non")}
                    checked={!formData.previsite}
                    onClick={() => toggleCheckbox("previsite", false)}
                  />
                </Stack>
              </Box>
              <Box>
                <Typography fontSize={13}>{i18n.t("devis")}</Typography>
                <Stack direction={"row"} mt={2}>
                  <CustomRadio
                    label={i18n.t("oui")}
                    containerStyle={{ marginRight: 5 }}
                    checked={formData.devis}
                    onClick={() => toggleCheckbox("devis", true)}
                  />
                  <CustomRadio
                    label={i18n.t("non")}
                    checked={!formData.devis}
                    onClick={() => toggleCheckbox("devis", false)}
                  />
                </Stack>
              </Box>
            </Stack>
            {/* <Box mt={5}>
              <Typography fontSize={13}>{i18n.t("mode_paiement")}</Typography>
              <Stack direction={"row"} mt={2}>
                <CustomRadio
                  label={i18n.t("oui")}
                  containerStyle={{ marginRight: 5 }}
                />
                <CustomRadio label={i18n.t("non")} />
              </Stack>
            </Box> */}
          </Grid>
        </Grid>
      </Box>
      <Stack
        p={3}
        my={3}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          background: "rgba(255, 255, 255, 0.25)",
          boxShadow: "0px 4px 4px rgba(139, 139, 139, 0.05)",
          borderRadius: "5px",
        }}
      >
        <CustomDropzone containerStyle={{ width: "500px" }} />
      </Stack>
      <Box
        p={3}
        mb={3}
        sx={{
          boxShadow: "0px 4px 4px rgba(139, 139, 139, 0.05)",
          borderRadius: "5px",
          backgroundColor: "white",
        }}
      >
        <Typography fontSize={12}>
          {capitalizeFirstLetter(i18n.t("gestionnaire_bara"))}
        </Typography>
        <Stack direction={"row"} justifyItems="space-between" spacing={5}>
          <SigninTextInput
            title={capitalizeFirstLetter(i18n.t("createur_bara"))}
            value={`${user?.nom || ""} ${user?.prenom || ""}`}
            disabled onChange={function (value: string): void {
              throw new Error("Function not implemented.");
            } }            />
          <FormControl fullWidth>
            <Typography
              color="black"
              sx={{
                fontSize: {
                  xs: 12,
                  md: 14,
                },
                mb: 1,
                mt: 3,
              }}
            >
              {capitalizeFirstLetter(i18n.t("affecter_bara"))}
            </Typography>
            <CustomPopper
              icon={
                <OutlinedInput
                  disabled
                  onClick={handleShowGrouilleur}
                  endAdornment={<ArrowDropDown />}
                  fullWidth
                  value={`${selectedGrouilleurBara?.nom ?? ""} ${
                    selectedGrouilleurBara?.prenom ?? ""
                  }`}
                />
              }
              onClose={handleShowGrouilleurBara}
              containerStyle={{ padding: 0, borderRadius: "5px" }}
              buttonStyle={{
                backgroundColor: "transparent",
                width: "100%",
                padding: 0,
              }}
            >
              <Box
                p={2}
                sx={{
                  height: "400px",
                  width: "400px",
                  position: "relative",
                  overflow: "auto",
                }}
              >
                <CustomSearchBar
                  placeholder={capitalizeFirstLetter(i18n.t("rechercher"))}
                  ctnStyle={{ width: "100%", marginBottom: 15 }}
                  onChange={(event) => onSearchGrouilleurBara(event.target.value)}
                />
                {grouilleurBaraSearch.map((item: any) => (
                  <BaraBossCard
                    key={item.id}
                    item={item}
                    onClick={() => onSelectGrouilleurBara(item)}
                  />
                ))}
              </Box>
            </CustomPopper>
          </FormControl>
        </Stack>
      </Box>
      <Stack direction={"row"} mt={5} justifyContent="space-between">
        <CustomButton
          title={capitalizeFirstLetter(i18n.t("cancel"))}
          rootStyle={{
            width: "250px",
            borderRadius: "6px",
            height: "42px",
            backgroundColor: "#EDEDED",
          }}
          titleStyle={{ color: theme.colors.black }}
        />
        <CustomButton
          title={capitalizeFirstLetter(i18n.t("save_bara"))}
          onClick={onSubmit}
          disabled={disabled}
          rootStyle={{
            width: "250px",
            borderRadius: "6px",
            height: "42px",
            backgroundColor: disabled
              ? theme.colors.disabled
              : theme.colors.primary,
          }}
          titleStyle={{ color: theme.colors.white }}
        />
      </Stack>
      <CustomLoader open={loading} handleClose={toggleLoader} />
    </Box>
  );
}

export default BaraForm;
